// ---------------------------------------------------------------------------
// Homepage sections.
//
// Each component reproduces one block of the original design; all of its copy,
// media, links and repeated items come from `content_blocks` / `content_items`.
// The registry at the bottom lets the homepage render whatever the database
// says, in whatever order it says.
// ---------------------------------------------------------------------------

import { useSite } from '../../context/SiteProvider.jsx';
import { Reveal } from '../common/Reveal.jsx';
import { RichText } from '../common/RichText.jsx';

/** A `.button` from a content item: label key, resolved href, style variant. */
function ContentButton({ item }) {
  const { t, href } = useSite();
  const { labelKey, href: link, variant = 'dark', icon = '↗', external } = item.data;
  const target = href(link);

  return (
    <a
      className={`button ${variant}`}
      href={target}
      {...(external ? { target: '_blank', rel: 'noopener' } : {})}
    >
      <span>{t(labelKey)}</span>
      <span>{icon}</span>
    </a>
  );
}

const itemsOf = (block, kind) => block.items.filter((item) => item.kind === kind);

export function HeroBlock({ block }) {
  const { t } = useSite();
  const s = block.settings;

  return (
    <section id="home" className="hero">
      <div className="hero-bg" aria-hidden="true" />
      <Reveal className="hero-copy">
        <p className="eyebrow">{t(s.eyebrowKey)}</p>
        <RichText as="h1" html={t(s.titleKey)} />
        <p className="lead">{t(s.textKey)}</p>
        <div className="hero-buttons">
          {itemsOf(block, 'button').map((item) => <ContentButton key={item.id} item={item} />)}
        </div>
        <div className="hero-meta">
          {itemsOf(block, 'meta').map((item) => <span key={item.id}>{t(item.data.labelKey)}</span>)}
        </div>
      </Reveal>
      <div className="hero-art hero-photo hero-logo-panel" aria-label={s.imageAlt}>
        <img src={s.image} alt={s.imageAlt} />
        <p>{s.caption}</p>
      </div>
    </section>
  );
}

export function MarqueeBlock({ block }) {
  return (
    <div className="marquee" aria-hidden="true">
      <div>{block.settings.text}</div>
    </div>
  );
}

export function AboutBlock({ block }) {
  const { t, href } = useSite();
  const s = block.settings;

  return (
    <section id="about" className="about section-pad">
      {s.index ? <div className="section-index">{s.index}</div> : null}
      <Reveal className="about-title">
        <p className="eyebrow">{t(s.eyebrowKey)}</p>
        <RichText as="h2" html={t(s.titleKey)} />
      </Reveal>
      <Reveal className="about-copy">
        <p className="large-copy">{t(s.textKey)}</p>
        <p>{t(s.subtextKey)}</p>
        <a className="arrow-link" href={href(s.linkHref)}>
          <span>{t(s.linkKey)}</span>
          <span>↗</span>
        </a>
      </Reveal>
    </section>
  );
}

export function QuantityGuideBlock({ block }) {
  const { t, href } = useSite();
  const s = block.settings;
  const levels = itemsOf(block, 'level');

  return (
    <section id="treatments" className="quantity-guide section-pad homepage-quantity-guide">
      <Reveal className="quantity-guide-copy">
        <p className="eyebrow">{t(s.eyebrowKey)}</p>
        <RichText as="h2" html={t(s.titleKey)} />
        <p>{t(s.textKey)}</p>
        {levels.length ? (
          <div className="quantity-levels">
            {levels.map((item) => <span key={item.id}>{item.data.label}</span>)}
          </div>
        ) : null}
        <p className="quantity-note">{t(s.noteKey)}</p>
        <a className="button dark" href={href(s.ctaHref)} target="_blank" rel="noopener">
          <span>{t(s.ctaKey)}</span>
          <span>↗</span>
        </a>
      </Reveal>
      <Reveal className="quantity-guide-image">
        <img src={s.image} alt={s.imageAlt} />
      </Reveal>
    </section>
  );
}

export function CatalogBlock({ block, children }) {
  const { t } = useSite();
  const s = block.settings;

  return (
    <section id="catalog" className="catalog section-pad">
      <Reveal className="section-head">
        <div>
          <p className="eyebrow">{t(s.eyebrowKey)}</p>
          <RichText as="h2" html={t(s.titleKey)} />
        </div>
        <p>{t(s.introKey)}</p>
      </Reveal>
      <div className="catalog-grid">{children}</div>
    </section>
  );
}

export function WhyBlock({ block }) {
  const { t } = useSite();
  const s = block.settings;

  return (
    <section id="why" className="why section-pad">
      <Reveal className="section-head">
        <div>
          <p className="eyebrow">{t(s.eyebrowKey)}</p>
          <RichText as="h2" html={t(s.titleKey)} />
        </div>
      </Reveal>
      <div className="why-grid">
        {itemsOf(block, 'card').map((item) => (
          <article key={item.id}>
            <b>{item.data.number}</b>
            <h3>{t(item.data.titleKey)}</h3>
            <p>{t(item.data.textKey)}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function WholesaleBlock({ block }) {
  const { t } = useSite();
  const s = block.settings;

  return (
    <section id="wholesale" className="wholesale">
      <Reveal className="wholesale-copy">
        <p className="eyebrow gold">{t(s.eyebrowKey)}</p>
        <RichText as="h2" html={t(s.titleKey)} />
        <p>{t(s.textKey)}</p>
        <div className="hero-buttons">
          {itemsOf(block, 'button').map((item) => <ContentButton key={item.id} item={item} />)}
        </div>
      </Reveal>
      <div className="wholesale-art" aria-hidden="true">
        <span>{s.artFrom}</span>
        <div className="route" />
        <b>{s.artTo}</b>
        <i>✦</i>
      </div>
    </section>
  );
}

export function ContactBlock({ block }) {
  const { t, settings } = useSite();
  const s = block.settings;

  return (
    <section id="contact" className="contact section-pad">
      <Reveal className="contact-intro">
        <p className="eyebrow">{t(s.eyebrowKey)}</p>
        <RichText as="h2" html={t(s.titleKey)} />
        <p>{t(s.textKey)}</p>
      </Reveal>
      <Reveal className="contact-details">
        <div>
          <span>{t(s.phoneLabelKey)}</span>
          <div className="contact-details-phones">
            <a href={`tel:${settings['contact.phone'] ?? ''}`}>{settings['contact.phone_display']}</a>
            {settings['contact.phone_secondary_display'] ? (
              <a href={`tel:${settings['contact.phone_secondary'] ?? ''}`}>
                {settings['contact.phone_secondary_display']}
              </a>
            ) : null}
          </div>
        </div>
        <div>
          <span>{t(s.addressLabelKey)}</span>
          <p>{t(settings['contact.address_key'] ?? 'address')}</p>
        </div>
        <div className="contact-buttons">
          {itemsOf(block, 'button').map((item) => <ContentButton key={item.id} item={item} />)}
        </div>
      </Reveal>
    </section>
  );
}

/** Blocks rendered outside <main> (topbar, footer) are handled by the layout. */
export const BLOCK_COMPONENTS = {
  hero: HeroBlock,
  marquee: MarqueeBlock,
  about: AboutBlock,
  quantity_guide: QuantityGuideBlock,
  why: WhyBlock,
  wholesale: WholesaleBlock,
  contact: ContactBlock,
};
